import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowDown, ArrowUp, GripVertical, Plus, Trash2 } from 'lucide-react';
import { OPTION_LISTS } from '@clawhire/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { cn } from '@/lib/utils';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { SkeletonLines } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { AdminNote, SettingsCard, SettingsSection } from '@/pages/settings/SettingsSection';

const LIST_META: Record<string, { label: string; description: string }> = {
  department: { label: 'Departments', description: 'Offered on positions.' },
  language: { label: 'Languages', description: 'Languages on positions and candidates (levels are free).' },
  skill: { label: 'Skills', description: 'Skill tags on positions and candidates.' },
  country: { label: 'Countries', description: 'Candidate countries.' },
};

const EMPTY_LIST: string[] = [];

/** Option lists (spec §4): one reorderable, editable list per key, saved with `PUT /options/:listKey`. */
export function OptionsSettings() {
  const { isAdmin } = useAuth();
  const options = useQuery({
    queryKey: queryKeys.options,
    queryFn: () => api.get<Record<string, string[]>>('/options'),
  });
  const keys = React.useMemo(() => {
    const set = new Set([...Object.keys(OPTION_LISTS), ...Object.keys(options.data ?? {})]);
    return Array.from(set);
  }, [options.data]);

  return (
    <SettingsSection
      title="Option lists"
      description="Values offered in the pick lists, in the order shown here. Removed values stay on existing records."
    >
      {isAdmin ? null : <AdminNote what="option lists" />}
      {options.isLoading ? (
        <SkeletonLines lines={6} />
      ) : options.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Option lists could not be loaded: {options.error.message}
        </p>
      ) : (
        keys.map((key) => (
          <OptionListEditor
            key={key}
            listKey={key}
            label={LIST_META[key]?.label ?? key}
            description={LIST_META[key]?.description}
            values={options.data?.[key] ?? EMPTY_LIST}
            readOnly={!isAdmin}
          />
        ))
      )}
    </SettingsSection>
  );
}

interface Item {
  id: string;
  value: string;
}

let seq = 0;
function toItem(value: string): Item {
  seq += 1;
  return { id: `opt-${seq}`, value };
}

function OptionListEditor({
  listKey,
  label,
  description,
  values,
  readOnly,
}: {
  listKey: string;
  label: string;
  description?: string;
  values: string[];
  readOnly: boolean;
}) {
  const queryClient = useQueryClient();
  const [items, setItems] = React.useState<Item[]>(() => values.map(toItem));
  const [dirty, setDirty] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const lastValues = React.useRef(values);

  // Follow the server when the list changes underneath and nothing is being edited.
  React.useEffect(() => {
    if (values !== lastValues.current) {
      lastValues.current = values;
      if (!dirty) setItems(values.map(toItem));
    }
  }, [values, dirty]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const change = (next: Item[]) => {
    setItems(next);
    setDirty(true);
  };
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    change(arrayMove(items, index, target));
  };
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = items.findIndex((i) => i.id === active.id);
    const to = items.findIndex((i) => i.id === over.id);
    if (from === -1 || to === -1) return;
    change(arrayMove(items, from, to));
  };
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.some((i) => i.value.trim().toLowerCase() === v.toLowerCase())) {
      toast.error(`"${v}" is already in the list`);
      return;
    }
    change([...items, toItem(v)]);
    setDraft('');
  };

  const save = useMutation({
    mutationFn: () =>
      api.put<string[]>(`/options/${encodeURIComponent(listKey)}`, {
        values: items.map((i) => i.value.trim()).filter(Boolean),
      }),
    onSuccess: (saved) => {
      const list = Array.isArray(saved) ? saved : items.map((i) => i.value.trim()).filter(Boolean);
      queryClient.setQueryData<Record<string, string[]>>(queryKeys.options, (old) => ({
        ...(old ?? {}),
        [listKey]: list,
      }));
      lastValues.current = list;
      setItems(list.map(toItem));
      setDirty(false);
      toast.success(`${label} saved`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const discard = () => {
    setItems(values.map(toItem));
    setDirty(false);
  };

  if (readOnly) {
    return (
      <SettingsCard title={label} description={description}>
        {values.length === 0 ? (
          <p className="text-sm text-muted-foreground">No values.</p>
        ) : (
          <ol className="flex flex-wrap gap-1.5" aria-label={label}>
            {values.map((v) => (
              <li key={v}>
                <Badge variant="secondary" className="font-normal">
                  {v}
                </Badge>
              </li>
            ))}
          </ol>
        )}
      </SettingsCard>
    );
  }

  return (
    <SettingsCard title={label} description={description}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <ol className="space-y-1" aria-label={label}>
            {items.map((item, index) => (
              <SortableRow
                key={item.id}
                item={item}
                index={index}
                count={items.length}
                onValue={(v) => change(items.map((i) => (i.id === item.id ? { ...i, value: v } : i)))}
                onMove={(d) => move(index, d)}
                onRemove={() => change(items.filter((i) => i.id !== item.id))}
              />
            ))}
          </ol>
        </SortableContext>
      </DndContext>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">No values yet.</p> : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          placeholder={`Add to ${label.toLowerCase()}…`}
          aria-label={`New value for ${label}`}
          className="h-9 max-w-xs"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9"
          onClick={add}
          disabled={!draft.trim()}
        >
          <Plus aria-hidden="true" /> Add
        </Button>
        {dirty ? (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Unsaved changes</span>
            <Button type="button" variant="ghost" size="sm" className="h-9" onClick={discard}>
              Discard
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-9"
              onClick={() => save.mutate()}
              loading={save.isPending}
            >
              Save {label.toLowerCase()}
            </Button>
          </div>
        ) : null}
      </div>
    </SettingsCard>
  );
}

function SortableRow({
  item,
  index,
  count,
  onValue,
  onMove,
  onRemove,
}: {
  item: Item;
  index: number;
  count: number;
  onValue: (v: string) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({
      id: item.id,
    });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-1 rounded-md border bg-background px-1.5 py-1',
        isDragging && 'z-10 shadow-md ring-1 ring-ring',
      )}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        className="cursor-grab rounded p-1 text-muted-foreground hover:bg-muted active:cursor-grabbing"
        aria-label={`Drag to reorder ${item.value || 'value'}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" aria-hidden="true" />
      </button>
      <Input
        value={item.value}
        onChange={(e) => onValue(e.target.value)}
        aria-label={`Value ${index + 1}`}
        className="h-8 flex-1"
      />
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="h-7 w-7"
        onClick={() => onMove(-1)}
        disabled={index === 0}
        aria-label={`Move ${item.value} up`}
      >
        <ArrowUp aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="h-7 w-7"
        onClick={() => onMove(1)}
        disabled={index === count - 1}
        aria-label={`Move ${item.value} down`}
      >
        <ArrowDown aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="h-7 w-7 text-muted-foreground hover:text-destructive"
        onClick={onRemove}
        aria-label={`Remove ${item.value}`}
      >
        <Trash2 aria-hidden="true" />
      </Button>
    </li>
  );
}
